import { classifyTarget, createMagneticTargets } from './cursorTargets';
import { createCursorMotion } from './cursorMotion';

// Pointer coordinates never pass through React state. One coalesced animation
// frame paints the latest position; the loop sleeps when its short tail expires.
export const createCursorEngine = (host, reduced) => {
  const targets = createMagneticTargets();
  const motion = createCursorMotion(host, reduced);
  const removers = [];
  let frame = 0, active = false, destroyed = false, down = false;
  let point = { x: 0, y: 0 }, offset = { x: 0, y: 0 };
  const listen = (node, name, handler, options = { passive: true, capture: true }) => {
    node.addEventListener(name, handler, options);
    removers.push(() => node.removeEventListener(name, handler, options));
  };
  const flag = (name, value) => { if (host.dataset[name] !== String(value)) host.dataset[name] = String(value); };
  const hide = () => {
    active = false; down = false; offset = { x: 0, y: 0 };
    cancelAnimationFrame(frame); frame = 0;
    document.documentElement.removeAttribute('data-bull-target');
    flag('visible', false); flag('hover', false); flag('magnetic', false); flag('running', false);
    motion.reset();
  };
  const schedule = () => {
    if (!frame && active && !destroyed) {
      flag('running', true);
      frame = requestAnimationFrame(tick);
    }
  };
  const tick = (now) => {
    frame = 0;
    if (!active || destroyed) return;
    if (document.hidden || document.pointerLockElement || point.x < 0 || point.y < 0 || point.x >= window.innerWidth || point.y >= window.innerHeight) { hide(); return; }
    const target = document.elementFromPoint(point.x, point.y);
    const mode = classifyTarget(target);
    if (mode.native) { hide(); return; }
    const pull = reduced || down ? { x: 0, y: 0 } : targets.pull(point, target, now);
    offset.x += (pull.x - offset.x) * 0.4;
    offset.y += (pull.y - offset.y) * 0.4;
    if (Math.abs(pull.x - offset.x) < 0.02) offset.x = pull.x;
    if (Math.abs(pull.y - offset.y) < 0.02) offset.y = pull.y;
    const trailing = motion.draw({ x: point.x + offset.x, y: point.y + offset.y }, now);
    flag('hover', mode.hover); flag('magnetic', Math.hypot(offset.x, offset.y) > 0.1);
    flag('visible', true);
    if (document.documentElement.getAttribute('data-bull-target') !== 'active') {
      document.documentElement.setAttribute('data-bull-target', 'active');
    }
    const settling = Math.hypot(pull.x - offset.x, pull.y - offset.y) > 0.02;
    if (trailing || settling) schedule();
    else flag('running', false);
  };
  const move = (event) => {
    if (event.pointerType !== 'mouse') { hide(); return; }
    point = { x: event.clientX, y: event.clientY };
    if (classifyTarget(event.target).native) { hide(); return; }
    active = true; schedule();
  };
  const press = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || classifyTarget(event.target).native) return;
    point = { x: event.clientX, y: event.clientY }; offset = { x: 0, y: 0 };
    active = true; down = true;
    motion.draw(point, performance.now()); motion.click(); schedule();
  };
  listen(document, 'pointermove', move);
  listen(document, 'pointerover', move);
  listen(document, 'pointerdown', press);
  listen(document, 'pointerup', () => { down = false; if (active) schedule(); });
  listen(document, 'pointercancel', hide);
  listen(document, 'pointerout', (event) => { if (!event.relatedTarget) hide(); });
  listen(window, 'blur', hide);
  listen(document, 'visibilitychange', () => { if (document.hidden) hide(); });
  listen(document, 'pointerlockchange', hide);
  listen(document, 'contextmenu', hide);
  listen(document, 'keydown', (event) => { if (['Tab', 'Escape', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) hide(); });
  listen(document, 'scroll', () => { targets.invalidate(); if (active) schedule(); });
  listen(window, 'resize', () => { targets.invalidate(); if (active) schedule(); });
  return { destroy: () => { destroyed = true; hide(); removers.forEach((remove) => remove()); targets.destroy(); } };
};